import { useEffect, useState } from 'react';
import { Check, ChevronDown, Info, Pencil, X } from 'lucide-react';
import { T, FONT, SERIF, fmtMoneyFull } from '../theme';
import { AsteriskNote, Card, Eyebrow, H1, Metric, SavingsBar, Stage, Sub } from '../primitives';
import { IncomeStackedChart } from '../charts/IncomeStackedChart';
import { submitClientUpdate } from '../../services/webReportApi';
import type { DetailsMode } from '../useDeckState';
import type { DetailsPersonVM, DetailsRowVM, DetailsVM } from '../../formatters/deckContent';
import type { ClientUpdateField, ReportEnrichment, WebReportPayload } from '../../types/webReport';

const ESTIMATE_DISCLAIMER =
  'Values marked with an asterisk are estimated. Social Security, pension, and other future benefits are projected using current statements and standard COLA assumptions; actual amounts will vary.';

const SAVINGS_COLORS = { teal: T.teal, blue: T.blue, navy: T.navy } as const;

interface Props {
  vm: DetailsVM;
  payload: WebReportPayload;
  enrichment: ReportEnrichment | null;
  mode: DetailsMode;
  setMode: (m: DetailsMode) => void;
  slug: string;
  interactive: boolean;
  /** Print: fixed chart width, animations off. */
  chartWidth?: number;
}

// Shared width for every editable value box (wages, retirement age, Social
// Security, pension, savings buckets) so they all read as the same-size field.
const FIELD_INPUT_WIDTH = 150;

/** Strip $ , / (and unit words) then parse; null when not a number. */
function parseNum(s: string): number | null {
  const cleaned = s.replace(/[$,/\s]/g, '').replace(/[a-zA-Z]+/g, '');
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** Act 2 — Details: validate household + per-person info, with client edit flow. */
export function ActDetails({ vm, payload, enrichment, mode, setMode, slug, interactive, chartWidth }: Props) {
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // (Re-)seed the edit buffer each time edit mode is entered.
  useEffect(() => {
    if (mode !== 'edit') return;
    const next: Record<string, string> = {};
    const seedVal = (path: string | null, raw: number | null) => {
      if (path) next[path] = raw != null ? String(raw) : '';
    };
    seedVal(vm.targetIncome.path, vm.targetIncome.raw);
    vm.savings?.buckets.forEach((b) => seedVal(b.path, b.amount));
    vm.people.forEach((p) => {
      seedVal(p.retireAge.path, p.retireAge.raw);
      p.rows.forEach((r) => seedVal(r.path, r.raw));
    });
    setEdits(next);
    setError(null);
  }, [mode, vm]);

  const editing = interactive && mode === 'edit';

  // Live household total as the client edits the three savings buckets.
  const liveSavingsTotal =
    vm.savings && editing
      ? vm.savings.buckets.reduce(
          (sum, b) => sum + (b.path ? parseNum(edits[b.path] ?? '') ?? 0 : b.amount),
          0
        )
      : vm.savings?.total ?? 0;

  const buildFields = (): ClientUpdateField[] => {
    const fields: ClientUpdateField[] = [];
    const pushField = (path: string | null, raw: number | null, label: string) => {
      if (!path) return;
      const value = edits[path];
      if (value == null) return;
      const parsed = parseNum(value);
      if (parsed == null) return;
      if (raw != null && parsed === raw) return;
      if (raw == null && parsed === 0) return;
      fields.push({ path, label, previous: raw, submitted: parsed });
    };
    pushField(vm.targetIncome.path, vm.targetIncome.raw, vm.targetIncome.label);
    vm.savings?.buckets.forEach((b) => pushField(b.path, b.amount, `${b.label} savings`));
    vm.people.forEach((p) => {
      pushField(p.retireAge.path, p.retireAge.raw, `${p.name} — Retirement age`);
      p.rows.forEach((r) => pushField(r.path, r.raw, `${p.name} — ${r.label}`));
    });
    return fields;
  };

  const handleCancel = () => {
    setNote('');
    setNoteOpen(false);
    setError(null);
    setMode('view');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await submitClientUpdate(slug, { fields: buildFields(), note: note.trim() || null });
      setMode('submitted');
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setError(
        message === 'rate-limited'
          ? "You've sent an update recently — please wait a few minutes before submitting again."
          : "Something went wrong sending your updates. Please try again, or mention it to your advisor."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (mode === 'submitted') {
    return (
      <Stage>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 420,
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: 30,
              background: T.growthSoft,
              color: T.growth,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 20,
            }}
          >
            <Check size={30} />
          </div>
          <h1 style={{ fontFamily: SERIF, fontSize: 32, fontWeight: 600, letterSpacing: '-0.01em', color: T.navy, margin: '0 0 12px' }}>
            Thank you
          </h1>
          <p style={{ fontSize: 15, color: T.gray, maxWidth: 440, lineHeight: 1.55, margin: '0 0 24px' }}>
            Your updated details{note ? ' and notes' : ''} have been sent to your advisor for review. You'll hear back
            within one business day.
          </p>
          <button
            onClick={() => {
              setMode('view');
              setNote('');
              setNoteOpen(false);
            }}
            style={{
              background: 'none',
              border: `1px solid ${T.navy}`,
              color: T.navy,
              cursor: 'pointer',
              fontFamily: FONT,
              fontSize: 13.5,
              fontWeight: 600,
              padding: '9px 20px',
              borderRadius: 8,
            }}
          >
            Back to details
          </button>
        </div>
      </Stage>
    );
  }

  const showChartCard = !(vm.startingMetrics === null && vm.people.length === 0);

  return (
    <Stage>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20 }}>
        <div style={{ flex: 1 }}>
          <Eyebrow>Details</Eyebrow>
          <H1>Let's validate your information</H1>
          <Sub>A quick confirmation of who's in the plan and what each person brings to it.</Sub>
        </div>
        {interactive &&
          (!editing ? (
            <button
              onClick={() => setMode('edit')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 8,
                border: `1px solid ${T.navy}`,
                background: 'transparent',
                color: T.navy,
                fontFamily: FONT,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                marginTop: 4,
              }}
            >
              <Pencil size={14} /> Edit details
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button
                onClick={handleCancel}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: `1px solid ${T.grayLine}`,
                  background: 'transparent',
                  color: T.gray,
                  fontFamily: FONT,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <X size={14} /> Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: 'none',
                  background: T.growth,
                  color: '#fff',
                  fontFamily: FONT,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: submitting ? 'default' : 'pointer',
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                <Check size={14} /> {submitting ? 'Sending…' : 'Submit for review'}
              </button>
            </div>
          ))}
      </div>

      {editing && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            background: T.tealSoft,
            border: `1px solid ${T.teal}`,
            borderRadius: 10,
            padding: '12px 16px',
            marginBottom: 18,
          }}
        >
          <Info size={17} color="var(--color-accent)" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13, color: T.tealDeep, lineHeight: 1.5 }}>
            You're editing your details. Submit to send changes to your advisor for review — nothing is updated in
            your plan until they confirm.
          </div>
        </div>
      )}

      {editing && error && (
        <div style={{ fontSize: 13, color: '#9F3A2F', marginBottom: 14, fontWeight: 600 }}>{error}</div>
      )}

      {/* row 1: household + total savings */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>
        <Card>
          <Eyebrow>Household</Eyebrow>
          <div style={{ padding: '11px 0' }}>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase',
                color: T.gray,
                marginBottom: 3,
              }}
            >
              {vm.targetIncome.label}
            </div>
            {editing && vm.targetIncome.path ? (
              <input
                value={edits[vm.targetIncome.path] ?? ''}
                inputMode="numeric"
                onChange={(e) => setEdits({ ...edits, [vm.targetIncome.path!]: e.target.value })}
                style={{
                  width: '100%',
                  fontFamily: FONT,
                  fontSize: 20,
                  fontWeight: 700,
                  color: T.navy,
                  border: `1px solid ${T.grayLine}`,
                  borderRadius: 6,
                  padding: '6px 10px',
                  background: T.card,
                  outline: 'none',
                }}
              />
            ) : (
              <div style={{ fontSize: 22, fontWeight: 700, color: T.navy }}>
                {vm.targetIncome.display}
                {vm.targetIncome.estimated && vm.targetIncome.display !== '—' && (
                  <span style={{ color: T.teal, fontWeight: 700, marginLeft: 2 }}>*</span>
                )}
              </div>
            )}
          </div>
        </Card>
        {vm.savings && (
          <Card>
            <Eyebrow>Total savings</Eyebrow>
            <div style={{ fontSize: 34, fontWeight: 700, color: T.navy, marginBottom: 16 }}>
              {fmtMoneyFull(editing ? liveSavingsTotal : vm.savings.total)}
              {!editing && vm.savings.estimated && (
                <span style={{ color: T.teal, fontWeight: 700, marginLeft: 2 }}>*</span>
              )}
            </div>
            {editing
              ? vm.savings.buckets.map(
                  (b) =>
                    b.path && (
                      <div
                        key={b.label}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                          padding: '7px 0',
                        }}
                      >
                        <span style={{ fontSize: 13, color: T.gray }}>{b.label}</span>
                        <input
                          value={edits[b.path] ?? ''}
                          inputMode="numeric"
                          onChange={(e) => setEdits({ ...edits, [b.path!]: e.target.value })}
                          style={{
                            width: FIELD_INPUT_WIDTH,
                            textAlign: 'right',
                            fontFamily: FONT,
                            fontSize: 14,
                            fontWeight: 600,
                            color: T.navy,
                            border: `1px solid ${T.grayLine}`,
                            borderRadius: 6,
                            padding: '5px 9px',
                            background: T.card,
                            outline: 'none',
                          }}
                        />
                      </div>
                    )
                )
              : vm.savings.buckets
                  .filter((b) => b.amount > 0)
                  .map((b) => (
                    <SavingsBar
                      key={b.label}
                      label={b.label}
                      amt={fmtMoneyFull(b.amount)}
                      pct={b.pct}
                      color={SAVINGS_COLORS[b.colorKey]}
                    />
                  ))}
          </Card>
        )}
      </div>

      {/* row 2: person cards */}
      {vm.people.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          {vm.people.map((p) => (
            <PersonCard key={p.name} p={p} editing={editing} edits={edits} setEdits={setEdits} />
          ))}
        </div>
      )}

      {/* collapsible advisor note — only visible in edit mode */}
      {editing && (
        <div style={{ marginTop: 18 }}>
          <button
            onClick={() => setNoteOpen((v) => !v)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: FONT,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: T.navy,
              fontSize: 13.5,
              fontWeight: 600,
              padding: 0,
            }}
          >
            <ChevronDown
              size={16}
              style={{ transform: noteOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s', color: T.teal }}
            />
            {noteOpen ? 'Hide note' : 'Add a note for your advisor'}
          </button>
          {noteOpen && (
            <div style={{ marginTop: 10, animation: 'deckFade .2s ease' }}>
              <div style={{ fontSize: 12.5, color: T.gray, marginBottom: 6, lineHeight: 1.4 }}>
                Anything else your advisor should know — life changes, questions, things on your mind. This goes
                directly to them with your updated details.
              </div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. We're thinking about retiring earlier than planned, or I'd like to understand the tax implications better..."
                rows={4}
                style={{
                  width: '100%',
                  fontFamily: FONT,
                  fontSize: 13.5,
                  color: T.navy,
                  lineHeight: 1.55,
                  border: `1px solid ${T.grayLine}`,
                  borderRadius: 8,
                  padding: '10px 14px',
                  background: T.card,
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* estimate disclaimer */}
      {vm.anyEstimates && (
        <div style={{ marginTop: 14 }}>
          <AsteriskNote text={ESTIMATE_DISCLAIMER} />
        </div>
      )}

      {/* starting plan chart. Print (fixed chartWidth): the 220px stat rail can't
          share the 720px printable width with the chart — stack it below as a
          4-up row, and keep the eyebrow attached to the card across page breaks. */}
      {showChartCard && (
        <div
          style={{ marginTop: 30 }}
          // Print: keep the block whole AND open it on its own page.
          className={chartWidth != null ? 'deck-chart-block print-page-break' : undefined}
        >
          <Eyebrow>Your starting plan</Eyebrow>
          <Card>
            <div style={{ fontWeight: 600, color: T.navy, marginBottom: 2, fontSize: 16 }}>
              Income to support your lifestyle
            </div>
            <div style={{ fontSize: 13, color: T.gray, marginBottom: 16 }}>
              No optimizations · conventional withdrawal order
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: vm.startingMetrics && chartWidth == null ? '1fr 220px' : '1fr',
                gap: 24,
              }}
            >
              <IncomeStackedChart payload={payload} enrichment={enrichment} width={chartWidth} />
              {vm.startingMetrics && (
                <div
                  style={
                    chartWidth != null
                      ? { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }
                      : { display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'center' }
                  }
                >
                  <Metric value={vm.startingMetrics.horizon} label="Time Horizon" small />
                  <Metric value={vm.startingMetrics.afterTaxIncome} label="After-Tax Cashflow" small />
                  <Metric value={vm.startingMetrics.savingsAtEnd} label="Savings at End" small />
                  <Metric value={vm.startingMetrics.totalPlanValue} label="Total Plan Value" small accent />
                </div>
              )}
            </div>
            <AsteriskNote text="Chart values and totals are projected using the assumptions on this plan; actuals will vary." />
          </Card>
        </div>
      )}
    </Stage>
  );
}

function PersonCard({
  p,
  editing,
  edits,
  setEdits,
}: {
  p: DetailsPersonVM;
  editing: boolean;
  edits: Record<string, string>;
  setEdits: (e: Record<string, string>) => void;
}) {
  return (
    <Card style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 19,
              background: T.tealSoft,
              color: T.teal,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {p.name[0]}
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.navy }}>{p.name}</div>
            {p.age != null && <div style={{ fontSize: 12.5, color: T.gray }}>Age {p.age}</div>}
          </div>
        </div>
        {editing && p.retireAge.path ? (
          // Retirement age is edited here, in place of the status pill.
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 11.5,
              fontWeight: 600,
              color: T.tealDeep,
              background: T.tealSoft,
              padding: '4px 6px 4px 12px',
              borderRadius: 20,
            }}
          >
            Retires at
            <input
              value={edits[p.retireAge.path] ?? ''}
              inputMode="numeric"
              aria-label={`${p.name} retirement age`}
              onChange={(e) => setEdits({ ...edits, [p.retireAge.path!]: e.target.value })}
              style={{
                width: 42,
                textAlign: 'center',
                fontFamily: FONT,
                fontSize: 13,
                fontWeight: 700,
                color: T.navy,
                border: `1px solid ${T.grayLine}`,
                borderRadius: 14,
                padding: '3px 4px',
                background: T.card,
                outline: 'none',
              }}
            />
          </span>
        ) : (
          <span
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              color: T.tealDeep,
              background: T.tealSoft,
              padding: '4px 10px',
              borderRadius: 20,
            }}
          >
            {p.status}
          </span>
        )}
      </div>
      {p.rows.map((r, i) => (
        <div
          key={r.label}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            padding: '8px 0',
            borderBottom: i < p.rows.length - 1 ? `1px solid ${T.grayLine}` : 'none',
            gap: 12,
          }}
        >
          <span style={{ fontSize: 13, color: T.gray, flexShrink: 0 }}>{r.label}</span>
          <span style={{ textAlign: 'right', flex: 1 }}>
            {editing && r.path ? (
              <input
                value={edits[r.path] ?? ''}
                inputMode="numeric"
                onChange={(e) => setEdits({ ...edits, [r.path!]: e.target.value })}
                style={{
                  width: FIELD_INPUT_WIDTH,
                  textAlign: 'right',
                  fontFamily: FONT,
                  fontSize: 14,
                  fontWeight: 600,
                  color: T.navy,
                  border: `1px solid ${T.grayLine}`,
                  borderRadius: 6,
                  padding: '4px 8px',
                  background: T.card,
                  outline: 'none',
                }}
              />
            ) : (
              <>
                <span style={{ fontSize: 14, fontWeight: 600, color: r.display === '—' ? T.grayLine : T.navy }}>
                  {r.display}
                  {r.estimated && r.display !== '—' && (
                    <span style={{ color: T.teal, fontWeight: 700, marginLeft: 2 }}>*</span>
                  )}
                </span>
                {r.note && <span style={{ display: 'block', fontSize: 11, color: T.gray }}>{r.note}</span>}
              </>
            )}
          </span>
        </div>
      ))}
    </Card>
  );
}
